import './global.module.scss';
import Style from './AppBarChart.module.scss';
import { forwardRef, ReactElement } from 'react';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import {
  AxisPropsForCartesianGridTicksGeneration,
  HorizontalCoordinatesGenerator,
  VerticalCoordinatesGenerator,
} from 'recharts/types/cartesian/CartesianGrid';
import { ChartOffsetInternal } from 'recharts/types/util/types';

export type BarAppearanceType =
  | {
      variant: 'Filled' | 'BottomBorder';
      color: string;
    }
  | {
      variant: 'Dashed';
      color?: never;
    };

export type BarData<T> = {
  key: string;
  value: number;
  appearance: BarAppearanceType;
  changes: number | null;
  source: T;
};

const CellResolver = (variant: BarAppearanceType, key: number) =>
  ({
    ['Dashed']: <Cell key={key} fill={'transparent'} stroke={'#A2ABAB'} height={-16} strokeDasharray={'4 4'} />,
    ['BottomBorder']: <Cell key={key} fill={variant.color} height={-1} />,
    ['Filled']: <Cell key={key} fill={variant.color} />,
  }[variant.variant]);

export const AppBarChart = <T,>({
  data,
  units,
  xAxisTickStep,
  renderTooltip,
}: {
  data: BarData<T>[];
  units?: string;
  xAxisTickStep?: number;
  renderTooltip?: (data: BarData<T>) => ReactElement;
}) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{
          top: 16,
          right: 24,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid
          stroke={'#DFE2E2'}
          strokeDasharray={3}
          horizontalCoordinatesGenerator={customHorizontalCoordinates}
          verticalCoordinatesGenerator={customVerticalCoordinates((xAxisTickStep ?? 0) + 1)}
        />
        <YAxis
          tick={(x) => CustomizedYAxisTick(x, units)}
          tickLine={false}
          tickCount={6}
          strokeWidth={0}
          allowDecimals={false}
        />
        <XAxis
          tick={CustomizedXAxisTick}
          strokeWidth={0}
          tickCount={data.length}
          dataKey={'key'}
          interval={xAxisTickStep}
        />
        <Tooltip
          content={<CustomTooltip renderTooltip={renderTooltip} />}
          isAnimationActive={false}
          cursor={{ fill: 'rgba(235, 239, 245, 0.5)' }}
          allowEscapeViewBox={{ x: false, y: true }}
          reverseDirection={{ x: false, y: true }}
          offset={12}
        />
        <Bar dataKey={'value'} radius={2}>
          {data.map((entry, i) => CellResolver(entry.appearance, i))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const customHorizontalCoordinates: HorizontalCoordinatesGenerator = (props: {
  yAxis: AxisPropsForCartesianGridTicksGeneration;
  width: number;
  height: number;
  offset: ChartOffsetInternal;
}) => {
  const tickCount = props.yAxis.tickCount ?? 0;
  const dY = props.offset.height / (tickCount * 2 - 2);
  return new Array(tickCount * 2 - 1).fill(0).map((_, i) => i * dY + props.offset.top);
};

const customVerticalCoordinates: (step: number) => VerticalCoordinatesGenerator =
  (step) =>
  (props: {
    xAxis: AxisPropsForCartesianGridTicksGeneration;
    width: number;
    height: number;
    offset: ChartOffsetInternal;
  }) => {
    const tickCount = props.xAxis.tickCount ?? 0;
    const dX = props.offset.width / tickCount;
    return [
      ...new Set([
        ...new Array(Math.round(tickCount / step) + 1).fill(0).map((_, i) => i * dX * step + props.offset.left),
        props.offset.left + props.offset.width,
      ]),
    ];
  };

const CustomTooltip = forwardRef<HTMLDivElement, any>((props, ref) => {
  if (!props.renderTooltip || !props.payload[0]?.payload.source) return null;
  return (
    <div ref={ref} className={Style.tooltipContainer}>
      {props.renderTooltip(props.payload[0]?.payload)}
    </div>
  );
});

const CustomizedYAxisTick = ({ x, y, payload, visibleTicksCount, index }: any, units?: string) => {
  const isTheLastTick = units && visibleTicksCount === index + 1;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={5} dx={-4} textAnchor="end" fontSize={12} fill="#A2ABAB">
        {isTheLastTick ? units : payload.value}
      </text>
    </g>
  );
};

const CustomizedXAxisTick = ({ x, y, payload }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text x={0} y={0} dy={10} textAnchor="middle" fontSize={12} fill="#A2ABAB">
      {payload.value}
    </text>
  </g>
);
